import type { UserTaskProfile } from "@workspace/model-presets";
import {
  getModelPreset,
  getTaskProfile,
  getTaskProfiles,
} from "@workspace/model-presets";
import { Plus, RotateCcw, Trash2, Wrench } from "lucide-react";
import { cn } from "../../utils";

import { MemoryToggle } from "@workspace/ui/components/memory";
import { Brain, History, Cpu, Mic, SlidersHorizontal } from "lucide-react";

import { PersonaSelector } from "./persona-selector";
import { VoicePicker } from "./voice-picker";
import type { Persona } from "@workspace/personas";
import type { ChatSettings, Model, ReasoningLevel } from "../../types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Slider,
  Switch,
} from "../ui";

const REASONING_LEVELS: {
  value: ReasoningLevel;
  label: string;
}[] = [
  { value: "off", label: "Off" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "on", label: "On" },
];

interface SettingsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: ChatSettings;
  onSettingChange: <K extends keyof ChatSettings>(
    key: K,
    value: ChatSettings[K],
  ) => void;
  onBatchSettingChange: (changes: Partial<ChatSettings>) => void;
  onReset: () => void;
  modelId: string;
  supportsReasoning?: boolean;
  onDeleteMessages?: () => void;
  deleteDisabled?: boolean;
  messageCount?: number;
  /**
   * Connected-tools dialog, moved here from the composer. It is a read-only
   * "what can this assistant reach" panel — reference material, not something
   * touched while composing — so it belongs with the other settings rather
   * than occupying a permanent slot next to the send button.
   */
  onToolsInfo?: () => void;
  /** Count of MCP tools the LB reports connected; hides the row when zero. */
  toolsAvailable?: number;
  /** A tool call is running — surfaced so the row still reports live activity. */
  isExecutingTools?: boolean;
  // Brain memory (tdd-0042) — per-thread toggle
  /** Memory feature available for this app (renders the Memory section). */
  memoryAvailable?: boolean;
  /** Memory on/off for the active thread. */
  memoryEnabled?: boolean;
  onToggleMemory?: () => void;
  /** Active thread is private — memory is hard-off and the toggle is disabled. */
  isPrivate?: boolean;
  /** Brain FE base URL for the "View and edit memories" link. */
  brainFeUrl?: string;
  /** ws(s):// voice service URL — enables the default-voice picker + preview. */
  voiceApiUrl?: string;
  // Persona selection (mirrored from the header so it's reachable in settings).
  personas?: Persona[];
  selectedPersonaId?: string | null;
  onPersonaChange?: (persona: Persona) => void;
  onCreatePersona?: () => void;
  onDeletePersona?: (personaId: string) => void;
  models?: Model[];
  modelsLoading?: boolean;
  modelsError?: string | null;
  /** Open the chat-history panel (closes settings first). */
  onOpenHistory?: () => void;
}

interface SliderSettingProps {
  label: string;
  description?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
}

function SliderSetting({
  label,
  description,
  value,
  min,
  max,
  step,
  onChange,
  formatValue = (v) => v.toString(),
}: SliderSettingProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <span className="text-sm text-muted-foreground font-mono">
          {formatValue(value)}
        </span>
      </div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
      />
    </div>
  );
}

/** Build a display name for the current settings state */
function getProfileDisplayName(
  modelId: string,
  profileKey: string | null,
  customProfiles: UserTaskProfile[],
): string {
  if (!profileKey) return "Custom";

  // Check custom profiles first
  if (profileKey.startsWith("custom:")) {
    const customId = profileKey.slice("custom:".length);
    const custom = customProfiles.find((p) => p.id === customId);
    return custom?.label ?? "Custom";
  }

  // Built-in profile
  const preset = getModelPreset(modelId);
  const profile = getTaskProfile(modelId, profileKey);
  if (preset && profile) {
    return `${preset.label} · ${profile.label}`;
  }
  return "Custom";
}

export function SettingsPanel({
  open,
  onOpenChange,
  settings,
  onSettingChange,
  onBatchSettingChange,
  onReset,
  modelId,
  supportsReasoning = true,
  onDeleteMessages,
  onToolsInfo,
  toolsAvailable = 0,
  isExecutingTools = false,
  deleteDisabled,
  messageCount = 0,
  memoryAvailable = false,
  memoryEnabled = false,
  onToggleMemory,
  isPrivate = false,
  brainFeUrl,
  voiceApiUrl,
  personas,
  selectedPersonaId,
  onPersonaChange,
  onCreatePersona,
  onDeletePersona,
  models,
  modelsLoading = false,
  modelsError = null,
  onOpenHistory,
}: SettingsPanelProps) {
  const builtInProfiles = getTaskProfiles(modelId);
  const customProfiles = settings.customProfiles ?? [];
  const hasProfiles = builtInProfiles.length > 0 || customProfiles.length > 0;
  const preset = getModelPreset(modelId);

  // Whether we're on a built-in or custom profile vs fully custom
  const isOnProfile = settings.taskProfileKey !== null;
  const isOnCustomProfile = settings.taskProfileKey?.startsWith("custom:") ?? false;
  const profileDisplayName = getProfileDisplayName(
    modelId,
    settings.taskProfileKey,
    customProfiles,
  );

  const handleTaskProfileChange = (profileKey: string) => {
    if (profileKey === "__custom__") {
      onSettingChange("taskProfileKey", null);
      return;
    }

    // Custom profile
    if (profileKey.startsWith("custom:")) {
      const customId = profileKey.slice("custom:".length);
      const custom = customProfiles.find((p) => p.id === customId);
      if (!custom) return;
      onBatchSettingChange({
        taskProfileKey: profileKey,
        temperature: custom.settings.temperature,
        topP: custom.settings.topP,
        topK: custom.settings.topK,
        minP: custom.settings.minP,
        presencePenalty: custom.settings.presencePenalty,
        repetitionPenalty: custom.settings.repetitionPenalty,
        reasoningLevel: custom.reasoning,
      });
      return;
    }

    // Built-in profile
    const profile = getTaskProfile(modelId, profileKey);
    if (!profile) return;
    onBatchSettingChange({
      taskProfileKey: profileKey,
      temperature: profile.settings.temperature,
      topP: profile.settings.topP,
      topK: profile.settings.topK,
      minP: profile.settings.minP,
      presencePenalty: profile.settings.presencePenalty,
      repetitionPenalty: profile.settings.repetitionPenalty,
      reasoningLevel: profile.reasoning,
    });
  };

  // When a generation param is manually changed, clear the active profile
  const handleParamChange = <K extends keyof ChatSettings>(
    key: K,
    value: ChatSettings[K],
  ) => {
    onSettingChange(key, value);
    if (settings.taskProfileKey !== null) {
      onSettingChange("taskProfileKey", null);
    }
  };

  // Save current settings as a new custom profile
  const handleSaveAsPreset = () => {
    const id = `custom-${Date.now()}`;
    const tierLabel = preset?.label ?? "Model";
    const newProfile: UserTaskProfile = {
      id,
      label: `${tierLabel} · Custom`,
      description: "User-created preset",
      reasoning: settings.reasoningLevel,
      sourceModelId: modelId,
      sourceTierLabel: tierLabel,
      settings: {
        temperature: settings.temperature,
        topP: settings.topP,
        topK: settings.topK,
        minP: settings.minP,
        presencePenalty: settings.presencePenalty,
        repetitionPenalty: settings.repetitionPenalty,
      },
    };
    onBatchSettingChange({
      customProfiles: [...customProfiles, newProfile],
      taskProfileKey: `custom:${id}`,
    });
  };

  // Delete a custom profile
  const handleDeleteCustomProfile = (customId: string) => {
    const updated = customProfiles.filter((p) => p.id !== customId);
    const changes: Partial<ChatSettings> = { customProfiles: updated };
    // If we're currently on this profile, switch to custom
    if (settings.taskProfileKey === `custom:${customId}`) {
      changes.taskProfileKey = null;
    }
    onBatchSettingChange(changes);
  };

  // Rename a custom profile
  const handleRenameCustomProfile = (customId: string, newLabel: string) => {
    const updated = customProfiles.map((p) =>
      p.id === customId ? { ...p, label: newLabel } : p,
    );
    onSettingChange("customProfiles", updated);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[400px] max-w-full overflow-y-auto px-4"
      >
        <SheetHeader>
          <SheetTitle>Settings</SheetTitle>
        </SheetHeader>

        <div className="flex-1 space-y-12 py-6 px-4">
          {/* Persona — label + selector on one line */}
          {onPersonaChange && onCreatePersona && models && (
            <div className="flex items-center justify-between gap-3">
              <Label className="flex items-center gap-1.5 text-sm shrink-0">
                <Cpu className="h-4 w-4 text-muted-foreground" />
                Persona
              </Label>
              <PersonaSelector
                personas={personas ?? []}
                selectedPersonaId={selectedPersonaId ?? null}
                onPersonaChange={onPersonaChange}
                onCreateNew={onCreatePersona}
                onDeletePersona={onDeletePersona}
                models={models}
                isLoading={modelsLoading}
                error={modelsError}
              />
            </div>
          )}

          {/* Advanced — preset → reasoning → sliders → save. Collapsed by default. */}
          <Accordion type="single" collapsible>
            <AccordionItem value="generation-params" className="border-none">
              <AccordionTrigger className="py-0 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-1.5">
                  <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                  Advanced
                </span>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div className="space-y-5">
                  {/* Preset / profile selector */}
                  {hasProfiles && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Preset</Label>
                        {isOnCustomProfile && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => {
                              const customId =
                                settings.taskProfileKey?.slice("custom:".length) ?? "";
                              handleDeleteCustomProfile(customId);
                            }}
                            title="Delete custom preset"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <Select
                        value={settings.taskProfileKey ?? "__custom__"}
                        onValueChange={handleTaskProfileChange}
                      >
                        <SelectTrigger className="w-full h-8 text-sm">
                          <SelectValue placeholder="Select preset" />
                        </SelectTrigger>
                        <SelectContent>
                          {builtInProfiles.length > 0 &&
                            preset &&
                            builtInProfiles.map(([key, profile]) => (
                              <SelectItem key={key} value={key}>
                                {preset?.label} · {profile.label}
                              </SelectItem>
                            ))}
                          {customProfiles.length > 0 &&
                            customProfiles.map((custom) => (
                              <SelectItem
                                key={`custom:${custom.id}`}
                                value={`custom:${custom.id}`}
                              >
                                {custom.label}
                              </SelectItem>
                            ))}
                          <SelectItem value="__custom__">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                      {isOnCustomProfile && (
                        <Input
                          value={profileDisplayName}
                          onChange={(e) => {
                            const customId =
                              settings.taskProfileKey?.slice("custom:".length) ?? "";
                            handleRenameCustomProfile(customId, e.target.value);
                          }}
                          className="h-8 text-sm"
                          placeholder="Preset name"
                        />
                      )}
                    </div>
                  )}

                  {/* Reasoning */}
                  {supportsReasoning && (
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-sm">Reasoning</Label>
                        <p className="text-xs text-muted-foreground">
                          {isOnProfile && !isOnCustomProfile
                            ? "Controlled by active preset"
                            : "Controls reasoning effort"}
                        </p>
                      </div>
                      <Select
                        value={settings.reasoningLevel}
                        onValueChange={(value) =>
                          handleParamChange("reasoningLevel", value as ReasoningLevel)
                        }
                      >
                        <SelectTrigger className="w-[120px] h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {REASONING_LEVELS.map((level) => (
                            <SelectItem key={level.value} value={level.value}>
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {supportsReasoning && settings.reasoningLevel !== "off" && (
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-sm">Show thinking</Label>
                        <p className="text-xs text-muted-foreground">
                          Thinking sections start expanded
                        </p>
                      </div>
                      <Switch
                        checked={settings.thinkingDisplayMode === "expanded"}
                        onCheckedChange={(checked) =>
                          onSettingChange(
                            "thinkingDisplayMode",
                            checked ? "expanded" : "collapsed",
                          )
                        }
                      />
                    </div>
                  )}

                  {/* Generation sliders */}
                  <div className="space-y-4">
                    <SliderSetting
                      label="Temperature"
                      description="Controls randomness. Lower = more focused, higher = more creative"
                      value={settings.temperature}
                      min={0}
                      max={2}
                      step={0.1}
                      onChange={(v) => handleParamChange("temperature", v)}
                    />

                    <SliderSetting
                      label="Top P"
                      description="Nucleus sampling. Consider tokens with top_p probability mass"
                      value={settings.topP}
                      min={0}
                      max={1}
                      step={0.05}
                      onChange={(v) => handleParamChange("topP", v)}
                    />

                    <SliderSetting
                      label="Top K"
                      description="Consider only top K tokens for sampling"
                      value={settings.topK}
                      min={1}
                      max={100}
                      step={1}
                      onChange={(v) => handleParamChange("topK", v)}
                    />

                    <SliderSetting
                      label="Min P"
                      description="Minimum probability threshold for token sampling"
                      value={settings.minP}
                      min={0}
                      max={1}
                      step={0.05}
                      onChange={(v) => handleParamChange("minP", v)}
                    />

                    <SliderSetting
                      label="Presence Penalty"
                      description="Penalize tokens based on presence in text"
                      value={settings.presencePenalty}
                      min={-2}
                      max={2}
                      step={0.1}
                      onChange={(v) => handleParamChange("presencePenalty", v)}
                    />

                    <SliderSetting
                      label="Repetition Penalty"
                      description="Multiplicative penalty for repeated tokens"
                      value={settings.repetitionPenalty}
                      min={0.5}
                      max={2}
                      step={0.05}
                      onChange={(v) => handleParamChange("repetitionPenalty", v)}
                    />

                    <SliderSetting
                      label="Repeat Penalty"
                      description="Penalize repeated tokens (additive)"
                      value={settings.repeatPenalty}
                      min={1}
                      max={2}
                      step={0.05}
                      onChange={(v) => handleParamChange("repeatPenalty", v)}
                    />

                    <SliderSetting
                      label="Frequency Penalty"
                      description="Penalize tokens based on frequency in text"
                      value={settings.frequencyPenalty}
                      min={-2}
                      max={2}
                      step={0.1}
                      onChange={(v) => handleParamChange("frequencyPenalty", v)}
                    />
                  </div>

                  {/* Save current params as a custom preset */}
                  {hasProfiles && !isOnProfile && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-1.5"
                      onClick={handleSaveAsPreset}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Save as preset
                    </Button>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Memory (Brain) — per-thread toggle (tdd-0042) */}
          {memoryAvailable && (
            <div className="space-y-2">
              <MemoryToggle
                enabled={memoryEnabled}
                onToggle={onToggleMemory}
                isPrivate={isPrivate}
              />
              {brainFeUrl && (
                <a
                  href={brainFeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline dark:text-blue-400"
                >
                  <Brain className="h-3 w-3" />
                  View and edit memories
                </a>
              )}
            </div>
          )}

          {/* Voice Settings — collapsed by default */}
          <Accordion type="single" collapsible>
            <AccordionItem value="voice-settings" className="border-none">
              <AccordionTrigger className="py-0 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-1.5">
                  <Mic className="h-4 w-4 text-muted-foreground" />
                  Voice
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pt-4">
                <div className="space-y-1">
                  <VoicePicker
                    voiceApiUrl={voiceApiUrl}
                    value={settings.defaultVoice}
                    onChange={(v) => onSettingChange("defaultVoice", v)}
                    label="Default voice"
                    defaultOptionLabel="System default"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used across all chats. Personas with their own voice keep it.
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Transcribe Only</Label>
                    <p className="text-xs text-muted-foreground">
                      Voice input only transcribes without LLM response (dev mode)
                    </p>
                  </div>
                  <Switch
                    checked={settings.disableLLM}
                    onCheckedChange={(checked) =>
                      onSettingChange("disableLLM", checked)
                    }
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Connected tools — closes settings, opens the tools dialog. */}
          {toolsAvailable > 0 && onToolsInfo && (
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => {
                onOpenChange(false);
                onToolsInfo();
              }}
            >
              <Wrench
                className={cn(
                  "h-4 w-4",
                  isExecutingTools && "animate-pulse text-amber-500",
                )}
              />
              Connected tools
              <span className="ml-auto text-xs text-muted-foreground">
                {isExecutingTools ? "running…" : toolsAvailable}
              </span>
            </Button>
          )}

          {/* Chat history — closes settings, opens the history sidebar */}
          {onOpenHistory && (
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => {
                onOpenChange(false);
                onOpenHistory();
              }}
            >
              <History className="h-4 w-4" />
              Chat history
            </Button>
          )}
        </div>

        {/* Was `md:hidden` — the desktop header carried its own context control.
            That header is gone, so this is the settings-side entry point at
            every width, alongside the token count in the composer. */}
        {onDeleteMessages && (
          <div className="px-4 pb-4">
            <Button
              variant="destructive"
              onClick={onDeleteMessages}
              disabled={deleteDisabled || messageCount === 0}
              className="w-full"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Manage Context
            </Button>
          </div>
        )}

        <SheetFooter>
          <Button variant="outline" onClick={onReset} className="w-full">
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
